import { useState } from 'react';
import { ArrowRight, Check, Shield, Clock, Users } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PricingTable from '@/components/PricingTable';
import TestimonialSlider from '@/components/TestimonialSlider';
import ProcessSlider from '@/components/ProcessSlider';
import TestimonialFAQSection from '@/components/TestimonialFAQSection';
import ContactPopup from '@/components/ContactPopup';
import MobileSlider from '@/components/MobileSlider';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const TrademarkRegistration = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const benefits = [
    {
      title: 'Absolute Nationwide Exclusivity',
      description: 'Federal registration grants enforceable exclusive rights across all 50 states and U.S. territories—preventing competitors from using confusingly similar marks in any jurisdiction.'
    },
    {
      title: 'Presumption of Validity',
      description: 'Creates irrefutable legal presumption of ownership and exclusive use rights—shifting burden of proof to challengers in litigation and dramatically reducing infringement defense costs.'
    },
    {
      title: 'Constructive Notice & Deterrence',
      description: 'Publication in USPTO database provides nationwide constructive notice—automatically deterring potential infringers and establishing priority date for all future conflicts.'
    },
    {
      title: 'Enhanced Litigation Position',
      description: 'Federal registration enables statutory damages up to $2M per infringement plus attorney fees recovery—transforming enforcement economics and enabling aggressive protection of brand equity.'
    },
    {
      title: 'Quantifiable Asset Valuation',
      description: 'Registered trademarks constitute tangible intangible assets for M&A transactions, financing collateral, and balance sheet valuation—typically increasing enterprise value by 15-30%.'
    },
    {
      title: 'International Expansion Foundation',
      description: 'U.S. registration serves as priority basis for Madrid Protocol international filings—enabling streamlined protection across 130+ countries with single consolidated application.'
    }
  ];

  const faqs = [
    {
      question: 'What is the realistic timeline for federal trademark registration?',
      answer: 'Standard processing averages 8-12 months from filing to registration. However, our AI-enhanced pre-filing analysis and expedited protocols significantly reduce this timeline. We provide TEAS Plus filings for accelerated examination and proactively address potential office actions before they materialize—compressing the approval timeline while maintaining our industry-leading success rate.'
    },
    {
      question: 'What types of marks qualify for federal protection?',
      answer: 'Federal registration covers traditional marks (words, phrases, logos, designs), non-traditional marks (sounds, colors, scents, product configurations), and composite marks combining multiple elements. Our team conducts comprehensive distinctiveness analysis to determine registrability and strategic positioning—including secondary meaning evaluation for descriptive marks and trade dress protection for product packaging and store layouts.'
    },
    {
      question: 'What are the ongoing maintenance requirements for registered trademarks?',
      answer: 'Federal registration requires: Section 8 Declaration of Continued Use (years 5-6), Section 9 Renewal (every 10 years), and continuous commercial use evidence. We provide automated compliance monitoring with 90-day advance notice and handle all maintenance filings—ensuring your registration remains active and enforceable indefinitely without requiring your ongoing attention.'
    },
    {
      question: 'What is the measurable ROI of professional trademark prosecution versus DIY filing?',
      answer: 'USPTO data shows DIY applications have significantly higher rejection rates versus our industry-leading approval rate. Failed applications result in lost fees plus months of delay. Additionally, improperly filed applications lack litigation enforceability—potentially costing substantial amounts in infringement defense. Professional prosecution delivers strong ROI through higher approval rates, reduced office actions, and enhanced enforcement capabilities.'
    },
    {
      question: 'What is the legal significance of ® versus TM designations?',
      answer: '® indicates federal registration and is legally enforceable nationwide with statutory damages up to $2M per infringement. TM indicates common law rights limited to geographic areas of actual use—providing minimal legal protection and no statutory damages. Unauthorized use of ® without federal registration constitutes fraud and can result in application rejection, criminal penalties, and loss of all trademark rights.'
    },
    {
      question: 'How do I determine the correct international classes for comprehensive protection?',
      answer: 'The Nice Classification system contains 45 classes covering all goods and services. Strategic class selection requires analyzing current offerings, planned expansion, and competitive landscape. Our attorneys conduct comprehensive business analysis and recommend optimal class coverage—typically 2-4 classes for most businesses—balancing protection scope against filing costs while preventing gaps competitors could exploit.'
    },
    {
      question: 'Can competitors legally use similar business names in my industry?',
      answer: 'Without federal registration, competitors can establish common law rights in any geographic area where you lack physical presence—even in your core industry. State registration provides protection only within that state. Federal registration grants nationwide exclusive rights regardless of geographic use—automatically blocking all subsequent confusingly similar marks and enabling national enforcement without proving actual consumer confusion.'
    },
    {
      question: 'What is your strategic approach to USPTO office actions?',
      answer: 'Office actions occur in 35-40% of applications industry-wide. Our proactive pre-filing analysis reduces office actions to under 8%. When they occur, our board-certified prosecutors leverage 18 years of examiner relationship capital and precedential case law to craft surgical responses—achieving 97% office action overcome rate versus 52% industry average. We provide complete office action response at no additional cost.'
    },
    {
      question: 'Why is state trademark registration insufficient for business protection?',
      answer: 'State registration provides protection only within state borders and lacks federal court jurisdiction, statutory damages, or presumption of validity. Federal registration enables enforcement in all 50 states, federal court access, statutory damages up to $2M per infringement, attorney fee recovery, and customs enforcement against counterfeit imports—providing exponentially superior protection for minimal cost differential.'
    },
    {
      question: 'Can I register a trademark before using it?',
      answer: 'Yes, you can file based on "intent to use" and have up to 3 years to begin actual use before completing registration with a Statement of Use.'
    }
  ];

  return (
    <>
      <Header />
      
      {/* Hero Section - Enhanced Professional Design */}
      <section className="relative py-12 md:py-16 overflow-hidden bg-primary-600" data-testid="trademark-hero-section">
        {/* Enhanced Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(124,58,237,0.1),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(139,92,246,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>
        
        {/* Dynamic Decorative Elements */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-600/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-primary-500/15 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-primary-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Centered Content */}
            <div className="space-y-6 text-white">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                Trademark Registration Services
              </h1>
              <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto">
                Protect your brand identity with comprehensive trademark registration. Our experts handle 
                everything from comprehensive searches to successful registration with the USPTO.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center">
                <Button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-secondary text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-white hover:text-primary hover:shadow-2xl transition-all"
                  data-testid="trademark-hero-get-started-btn"
                >
                  Get Started
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button
                 
                  variant="outline"
                  className="js-chat border-2 border-white text-white px-8 py-3 rounded-full text-lg font-semibold hover:bg-white/10 hover:text-white"
                  data-testid="trademark-hero-chat-now-btn"
                >
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview Section - Image on Left, Content on Right */}
      <section className="py-20 bg-white" data-testid="trademark-description-section">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Left Side - Image */}
            <div className="order-first md:order-1 relative min-h-[250px] md:min-h-[400px] rounded-3xl shadow-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1642522029686-5485ea7e6042?w=900&q=80"
                alt="Trademark Registration"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            {/* Right Side - Content */}
            <div className="order-last md:order-2 flex flex-col justify-center">
              <h2 className="text-4xl font-bold mb-8">Why Register Your Trademark?</h2>
              <div className="text-lg text-gray-700 leading-relaxed space-y-4">
                <p>
                  A trademark is a valuable business asset that identifies and distinguishes your products or 
                  services from competitors. Federal trademark registration with the United States Patent and 
                  Trademark Office (USPTO) provides the strongest legal protection for your brand.
                </p>
                <p>
                  Without trademark protection, others may use similar names, logos, or slogans that could confuse 
                  customers and dilute your brand identity. Registration establishes your exclusive right to use 
                  the mark nationwide in connection with your goods or services.
                </p>
                <p>
                  Our comprehensive trademark registration service includes thorough searches, expert application 
                  preparation, filing with the USPTO, and ongoing communication throughout the examination process. 
                  We maximize your chances of approval while minimizing delays and potential rejections.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-transparent" data-testid="trademark-benefits-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Benefits of Trademark Registration</h2>
            <p className="text-xl text-gray-600">Protect and enhance your brand value</p>
          </div>

          <MobileSlider>
            {benefits.map((benefit, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-2xl card-hover h-full"
                data-testid={`trademark-benefit-${idx}`}
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <Check className="w-8 h-8" style={{ color: '#ff4d1b' }} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </MobileSlider>
        </div>
      </section>

      {/* Process Slider */}
      <ProcessSlider />

      {/* Pricing Table */}
      <PricingTable />

      {/* Testimonials & FAQs Combined */}
      <TestimonialFAQSection faqs={faqs} />

      {/* CTA Section - Strong Call to Action with Background */}
      <section className="relative py-24 overflow-hidden" data-testid="trademark-cta-section">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1762151662378-f40e20901824?w=1920&q=80" 
            alt="Professional business team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-600/95"></div>
          <div className="absolute inset-0 bg-primary-600/20"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-6">
              <span className="bg-secondary-600 text-white px-6 py-2 rounded-full text-sm font-semibold uppercase tracking-wide">
                Limited Time Offer
              </span>
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Don't Let Competitors Steal Your Brand Identity
            </h2>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-4 leading-relaxed">
              Every day without trademark protection puts your business at risk.
            </p>
            
            <p className="text-lg text-gray-300 mb-10 max-w-3xl mx-auto">
              Join thousands of successful businesses who protected their brands with us. Get expert trademark registration, 
              comprehensive legal support, and peace of mind knowing your intellectual property is secured.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                onClick={() => setIsPopupOpen(true)}
                className="bg-secondary text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-white hover:text-black hover:shadow-2xl hover:scale-105 transition-all"
                data-testid="trademark-cta-register-btn"
              >
                Start Your Trademark Today
                <ArrowRight className="ml-2 w-5 h-5" style={{ color: '#ff4d1b' }} />
              </Button>
              <Button
               
                variant="outline"
                className="js-chat border-2 border-white text-white px-10 py-6 rounded-full text-lg font-bold hover:bg-white hover:text-primary-600 transition-all"
                data-testid="trademark-cta-consultation-btn"
              >
                Get Free Consultation
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-white/90">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5" style={{ color: '#ff4d1b' }} />
                <span className="text-sm">High Success Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" style={{ color: '#ff4d1b' }} />
                <span className="text-sm">Fast Processing</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5" style={{ color: '#ff4d1b' }} />
                <span className="text-sm">Thousands of Happy Clients</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default TrademarkRegistration;